import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiSearch, FiFilter, FiMapPin, FiBriefcase, FiUsers, FiX, FiChevronDown } = FiIcons;

const StreamSearchHeader = ({ onSearch, filters, setFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value, filters);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onSearch(searchTerm, newFilters);
  };

  const clearFilters = () => {
    const cleared = { location: '', workPlace: '', familyName: '' };
    setFilters(cleared);
    onSearch(searchTerm, cleared);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="max-w-4xl mx-auto mb-16">
      <div className="relative group">
        <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none text-text-muted group-focus-within:text-accent-gold transition-colors">
          <SafeIcon icon={FiSearch} className="w-5 h-5" />
        </div>
        <input
          type="text"
          placeholder="Search by Deceased Name, Family, or Event Title..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="w-full pl-16 pr-32 py-6 bg-white border-2 border-brand-dark rounded-full text-accent-navy placeholder:text-text-muted/50 focus:outline-none focus:border-accent-gold shadow-sm focus:shadow-xl transition-all font-medium text-lg"
        />
        <div className="absolute inset-y-2 right-2 flex items-center gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full text-xs font-black uppercase tracking-widest transition-all ${
              isExpanded || hasActiveFilters 
                ? 'bg-accent-navy text-white' 
                : 'bg-brand-light text-text-muted hover:bg-white hover:shadow-md'
            }`}
          >
            <SafeIcon icon={FiFilter} />
            <span className="hidden sm:inline">Advanced Filters</span>
            <SafeIcon icon={FiChevronDown} className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-4 bg-white border border-brand-dark rounded-[32px] p-8 shadow-2xl overflow-hidden relative"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Location Filter */}
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy/60 flex items-center gap-2">
                  <SafeIcon icon={FiMapPin} className="text-accent-gold" /> Location
                </label>
                <input
                  type="text"
                  placeholder="City or State..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-full px-5 py-3.5 bg-brand-light/50 border border-brand-dark rounded-2xl text-sm focus:outline-none focus:border-accent-gold transition-all"
                />
              </div>

              {/* Work Place Filter */}
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy/60 flex items-center gap-2">
                  <SafeIcon icon={FiBriefcase} className="text-accent-gold" /> Place of Work
                </label>
                <input
                  type="text"
                  placeholder="Company, School, etc..."
                  value={filters.workPlace}
                  onChange={(e) => handleFilterChange('workPlace', e.target.value)}
                  className="w-full px-5 py-3.5 bg-brand-light/50 border border-brand-dark rounded-2xl text-sm focus:outline-none focus:border-accent-gold transition-all"
                />
              </div>

              {/* Family Name Filter */}
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy/60 flex items-center gap-2">
                  <SafeIcon icon={FiUsers} className="text-accent-gold" /> Family Name
                </label>
                <input
                  type="text"
                  placeholder="e.g., Thompson, Miller..."
                  value={filters.familyName}
                  onChange={(e) => handleFilterChange('familyName', e.target.value)}
                  className="w-full px-5 py-3.5 bg-brand-light/50 border border-brand-dark rounded-2xl text-sm focus:outline-none focus:border-accent-gold transition-all"
                />
              </div>
            </div>

            {hasActiveFilters && (
              <div className="mt-8 pt-6 border-t border-brand-light flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-[10px] font-black uppercase tracking-widest text-red-500 hover:text-red-600 flex items-center gap-2 transition-colors"
                >
                  <SafeIcon icon={FiX} /> Clear All Filters
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StreamSearchHeader;